/**
 * @file DraftExpiryDialog.tsx
 * @description «Заполнить сроки» в черновике приёмки: дата изготовления и срок
 * строки без срока — правкой строки (`PATCH …/items/:itemId`), без «удалите и
 * добавьте заново» (phase19.22, W20; ui-design §7).
 *
 * Поля — те же `ExpiryFields`, что в панели добавления: автосрок от даты
 * изготовления по карточке/виду товара, пустой срок подсвечен. Строк без срока
 * несколько — после сохранения страница открывает следующую («1 из 3»).
 *
 * phase19.31 (Б15): то же окно — «Изменить срок» у строки черновика, где срок
 * уже есть (исправить дату без удаления строки); оба поля в одну линию.
 * Сервер принимает у черновика обе даты (`PATCH …/items/:itemId`).
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";

import { ExpiryFields, type ExpiryValue } from "./ExpiryFields";
import { useSkuExpiryPolicy } from "./useSkuExpiryPolicy";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { formatSkuName, type GoodsReceiptItemDto } from "../../lib/api-wms";

interface Props {
  item: GoodsReceiptItemDto;
  /** «1 из 3» — сколько строк без срока осталось заполнить. */
  progress?: string;
  onSubmit: (dates: { manufacturedAt: string | null; expiresAt: string | null }) => Promise<boolean>;
  onClose: () => void;
}

/** Дата из DTO (`2027-03-15T00:00:00.000Z`) → значение `input[type=date]`. */
function dateValue(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : "";
}

export function DraftExpiryDialog({ item, progress, onSubmit, onClose }: Props) {
  const { policy } = useSkuExpiryPolicy(item.sku?.product.id);
  const [value, setValue] = useState<ExpiryValue>({
    manufacturedAt: dateValue(item.manufacturedAt),
    expiresAt: dateValue(item.expiresAt),
    expiresManual: !!item.expiresAt,
  });
  const [saving, setSaving] = useState(false);
  const canSave = !!value.expiresAt || !!value.manufacturedAt;
  // Б15: у строки со сроком окно открывается как «Изменить срок».
  const editing = Boolean(item.expiresAt || item.manufacturedAt);

  async function submit() {
    if (!canSave || saving) return;
    setSaving(true);
    // Пустой срок при дате изготовления — `null`: сервер посчитает его сам.
    await onSubmit({ manufacturedAt: value.manufacturedAt || null, expiresAt: value.expiresAt || null });
    setSaving(false);
  }

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent fullScreen="lg" className="lg:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Изменить срок годности" : "Срок годности строки"}
            {progress ? ` · ${progress}` : ""}
          </DialogTitle>
          <DialogDescription>
            {item.lineNo ? `${item.lineNo}. ` : ""}
            {formatSkuName(item.sku)} · {item.quantity}
          </DialogDescription>
        </DialogHeader>
        <ExpiryFields value={value} onChange={setValue} policy={policy} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {editing ? "Отмена" : "Позже"}
          </Button>
          <Button onClick={() => void submit()} disabled={!canSave || saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            <span className={saving ? "ml-2" : ""}>Сохранить срок</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
